<template>
  <v-layout column>
    <div class="title font-weight-medium">CSV Upload</div>
    <v-stepper :value="step">
      <v-stepper-header>
        <v-stepper-step step="1">Pre-upload</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step step="2">Upload</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <div class="content">
            <div class="important font-weight-bold">Important Notice:</div>
            <v-layout>
              <v-flex class="infos font-weight-medium">
                The CSV header must contain the following columns:<br><br>
                - batch_number<br>
                - supplier_lot_1<br>
                - supplier_lot_2<br>
                - mixing_time<br><br>
                Mixing time format: YYYY-MM-DD hh:mm:ss<br>
                Please use comma (,) as the delimiter in your CSV file.
              </v-flex>
              <v-flex>
                <div class="file-upload-ctn">
                  <v-layout @click="clickOnFileInput($event, 'csv-file')" class="upload-btn" align-center justify-center column>
                    <div v-if="!csv || !csv.name" class="text-xs-center">
                      <v-icon x-large color="primary">cloud_upload</v-icon>
                      <div class="text-uppercase font-weight-bold">Choose file</div>
                      <div class="format-hint">
                        Supported formats:<br>
                        CSV
                      </div>
                    </div>

                    <div v-if="csv && csv.name" class="text-xs-center">
                      <v-icon x-large color="primary">cloud_upload</v-icon>
                      <div class="text-uppercase font-weight-bold">Change File</div>
                      <div class="format-hint">
                        {{csv.name}}
                      </div>
                    </div>

                  </v-layout>
                  <input v-show="false" accept=".csv" @change="onFileChange($event, csv)" type="file" ref="csv-file" />
                </div>
              </v-flex>
            </v-layout>
          </div>
          <v-layout class="footer">
            <v-flex grow>
              <v-btn @click="downloadCSVTemplate()" flat color="primary">Download CSV Template&nbsp;&nbsp;<v-icon>cloud_download</v-icon></v-btn>
            </v-flex>
            <v-flex shrink>
              <v-btn @click="handleClose()" flat color="primary">Cancel</v-btn>
              <v-btn @click="parseCSV()" raised color="primary">Next</v-btn>
            </v-flex>
          </v-layout>
        </v-stepper-content>

        <v-stepper-content class="custom-stepper" step="2">
          <div class="loading" v-show="!completed">
            <v-progress-linear v-show="!completed" :indeterminate="true"></v-progress-linear>
            <v-layout align-center justify-center row fill-height>
              <v-flex shrink class="infos font-weight-bold">Uploading... ({{this.uploadCount}} / {{this.line}}) </v-flex>
            </v-layout>
          </div>

          <div v-if="completed && uploadErrors.length" class="content">
            <div class="important font-weight-bold" >Errors!</div>

            <div class="infos font-weight-medium">Something is wrong with your CSV. Please fix the issues below and try again.<br><br></div>
            <div v-for="(line, index) in uploadErrors" :key="`errors-${index}`" class="infos font-weight-medium">
              {{line}}
            </div>
          </div>

          <div v-if="completed && !uploadErrors.length" class="content">
            <div class="important font-weight-bold">Upload Complete!</div>

            <div class="infos font-weight-medium">{{this.line}} Mixing batches have been created.</div>
          </div>
          <v-layout class="footer" row>
            <v-flex grow>
              <v-btn @click="resetState()" flat color="primary">Back</v-btn>
            </v-flex>
            <v-flex shrink>
              <v-btn @click="handleClose()" raised color="primary">Close</v-btn>
            </v-flex>
          </v-layout>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-layout>
</template>

<script>

import * as Papa from 'papaparse'
import { mapGetters } from 'vuex'
import slugify from '@sindresorhus/slugify'

export default {
  name: 'BatchUploadCSV',
  data () {
    return {
      step: 1,
      headerLine: 'batch_number,tenderloin_lot,shinbone_marrow_lot,mixing_time',
      uploadErrors: [],
      uploadData: [],
      uploadCount: 0,
      csv: {},
      line: 0,
      completed: false
    }
  },
  computed: mapGetters({
    packagingPlant: 'packagingPlant'
  }),
  methods: {

    handleClose () {
      this.$emit('on-close-dialog')
      this.resetState()
    },

    resetState () {
      this.step = 1
      this.uploadErrors = []
      this.uploadData = []
      this.line = 0
      this.completed = false
    },

    parseCSV () {
      if (!this.csv.name) {
        this.$store.dispatch('showSnackBar', { message: 'Please select a CSV file' })
        return
      }
      this.step = 2
      Papa.parse(this.csv, {
        skipEmptyLines: true,
        fastMode: true,
        header: true,
        step: (row, parser) => {
          if (this.line === 0 && row.meta.fields.join(',') !== this.headerLine) {
            this.uploadErrors.push(`Invalid file header. Please make sure all mandatory fields are present or download the CSV template.`)
            parser.abort()
          }

          if (row.errors.length) {
            row.errors.some((err) => {
              this.uploadErrors.push(`Line ${err.row + 1}: ${err.message}`)
              parser.abort()
            })
          }

          for (var key in row.data[0]) {
            if (!row.data[0][key]) {
              this.uploadErrors.push(`Line ${this.line + 1}: No value found for column ${key}`)
              parser.abort()
            }
          }

          this.uploadData.push(row.data[0])
          this.line++
        },
        complete: (results) => {
          this.startUpload()
        }
      })
    },

    assignLotToBatch (batches) {
      return new Promise((resolve, reject) => {
        let promises = []

        batches.forEach((batch, index) => {
          batch.lots.forEach((lotId) => {
            promises.push(this.$store.dispatch('assignLotToBatch', { batchId: batch.id, lot: lotId, csv: true }))
          })
        })

        Promise.all(promises).then(() => {
          resolve()
        }).catch((err) => {
          reject(err)
        })
      })
    },

    slugifyId (id) {
      return slugify(id, { lowercase: false, decamelize: false })
    },

    uploadByBatch (index) {
      let arrayPromises = []
      let idCheckArray = []
      let batchesToAssign = []
      let array = JSON.parse(JSON.stringify(this.uploadData))
      array = array.splice(index, 3)
      this.uploadCount = index

      if (!array.length) {
        this.completed = true
        this.$store.dispatch('loadBatchList')
        return
      }

      array.forEach((batch) => {
        let id = this.slugifyId(batch.batch_number)
        idCheckArray.push(this.$store.dispatch('checkBatchId', { id: id }))
      })

      Promise.all(idCheckArray).then(async (res) => {
        array.forEach((batch, arrayIndex) => {
          if (!batch.mixing_time.match(/^\d\d\d\d[-,/](0?[1-9]|1[0-2])[-,/](0?[1-9]|[12][0-9]|3[01]) (00|0[0-9]|[0-9]|1[0-9]|2[0-3]):([0-9]|[0-5][0-9]):([0-9]|[0-5][0-9])$/g)) {
            this.uploadErrors.push(`Line: ${index + arrayIndex + 2} Incorrect mixing time format. Please make sure the format is: YYYY-MM-DD hh:mm:ss`)
            this.completed = true
            return
          }

          let formToSend = {}
          formToSend.id = this.slugifyId(batch.batch_number)
          formToSend.location = this.packagingPlant.id
          formToSend.dosing_at = new Date(batch.mixing_time)
          formToSend.lots = []
          formToSend.lots.push(batch.shinbone_marrow_lot)
          formToSend.lots.push(batch.tenderloin_lot)

          if (res[arrayIndex] !== 'exists') {
            arrayPromises.push(this.$store.dispatch('createBatch', { batch: formToSend }))
          }
          batchesToAssign.push(formToSend)
        })

        if (!arrayPromises.length) {
          // exit is error detected
          if (this.completed) {
            return
          }
          try {
            await this.assignLotToBatch(batchesToAssign)

            this.uploadByBatch(index + 3)
            return
          } catch (err) {
            if (err.response.status === 400) {
              if (err.response.data.id) {
                this.uploadErrors.push(`Lot number ${err.response.data.id[0].split(/"/)[1]} does not exist. `)
              }
            }
            this.completed = true
            return
          }
        }

        Promise.all(arrayPromises).then(async (batches) => {
          try {
            await this.assignLotToBatch(batchesToAssign)
            this.uploadByBatch(index + 3)
          } catch (err) {
            if (err.response.status === 400) {
              console.log(err.response.data.id)
              if (err.response.data.id) {
                this.uploadErrors.push(`Lot number ${err.response.data.id[0].split(/"/)[1]} does not exist. `)
              }
            }
            this.completed = true
          }
        }).catch((err) => {
          console.log('caught err')
          console.log(err)
        })
      }).catch((err) => {
        console.log('catch err for id check')
        console.log(err)
      })
    },

    startUpload () {
      this.uploadByBatch(0)
    },

    downloadCSVTemplate () {
      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += this.headerLine
      const data = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', data)
      link.setAttribute('download', 'batch_upload.csv')
      link.click()
    },

    clickOnFileInput (event, inputName) {
      event.stopImmediatePropagation()
      this.$refs[inputName].click()
    },

    onFileChange (event, csv) {
      setTimeout(() => {
        this.csv = event.target.files[0]
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.title{
  padding: 25px;
  background: white;
  position: relative;
  z-index: 99;
}

.v-stepper{
  position: relative;
  top: -1px;
}

.important{
  font-size: 19px;
  margin-bottom: 15px;
}

.infos{
  color: black;
  opacity: 0.6;
  font-size: 14px;
}

.content{
  padding-bottom: 40px;
}

.upload-btn{
  width: 100%;
  height: 100%;
  background: #fafafa;
  padding: 15px;
  cursor: pointer;
  .v-icon{
    width: 35px;
  }
  .format-hint{
    word-break: break-all;
    margin-top: 15px;
  }
  .text-uppercase{
    color: #378e07;
    font-size: 14px;
  }
}

.custom-stepper{
  padding: 0px;
  .v-progress-linear{
    margin-top: 0px;
  }
  .footer{
    margin: 0px 24px 16px 24px;
  }
  .content{
    padding: 24px 16px;
  }
  .loading{
    .infos{
      padding: 50px 0px;
      color: #378e07;
      font-size: 20px;
      opacity: 1;
    }
  }
}

</style>
